import { setLocale } from '@va/dashboard/actions/ui';
import { ChatwootWidget, NotificationV2 } from '@va/dashboard/components';
import { DashboardGlobalFiltersConfigProvider, DashboardReportBlockConfigProvider } from '@va/dashboard/shared/filters';
import { useDefaultTranslationOptions } from '@va/dashboard/util-hooks';
import {
  fetchTranslationsFnWithFallback,
  getTranslationsUrl,
  TranslationsProvider,
  useLocalizationContext,
} from '@va/localization';
import { appHistory } from '@va/shared/router';
import { ConfirmationDialogContextProvider, RouterWarningProvider } from '@va/shared/util-confirmation';
import { setPaddleCallbackEvent } from '@va/standalone/shared/actions';
import { T3_URL_PARAMS } from '@va/standalone/shared/constants';
import { isUserLoggedInWithCustomUrl } from '@va/standalone/shared/helpers';
import '@va/styles/index.scss';
import '@va/styles/tailwind.css';
import { LoaderBar } from '@va/ui/design-system';
import { ErrorBoundary } from '@va/util/components';
import { handleAAASQueryParams, updateUrlParameter } from '@va/util/helpers';
import { DashboardAppWorker } from '@va/util/misc';
import Config from 'Config';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import { PropsWithChildren, useEffect } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { initializeApp } from './initializeApp';
import Routes from './router/routes';

const Paddle: Record<string, any> | undefined = window.Paddle;

// VISA-5819 - internal functionality for the Marketing team
handleAAASQueryParams();

const store = initializeApp();

const fetchTranslationsFn = async (locale = 'en') => {
  const customDomainOrigin = isUserLoggedInWithCustomUrl() ? window.location.origin : undefined;

  return fetchTranslationsFnWithFallback(getTranslationsUrl(locale, customDomainOrigin), locale);
};

const StandaloneApp = () => {
  //TODO Move Paddle.setup to UpgradePlan component when deleting old checkout code
  useEffect(() => {
    Paddle?.Setup({
      vendor: Config.paddleVendorId,
      eventCallback: (data: any) => {
        store.dispatch(setPaddleCallbackEvent(data));
      },
    });
    Config && Config.paddleSandbox && Paddle?.Environment.set('sandbox');
  }, []);

  useEffect(() => {
    //call run method on worker instance to track all visits on visa app
    DashboardAppWorker.run();
  }, []);

  return (
    <Providers>
      <App />
    </Providers>
  );
};

export default StandaloneApp;

const Providers: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <FlagsmithProvider flagsmith={flagsmith}>
      <SWRConfig value={{ revalidateIfStale: false, revalidateOnFocus: false }}>
        <SkeletonTheme baseColor='#F9F9F9' highlightColor='#EEEEEE'>
          <Provider store={store}>
            <TranslationsProvider
              onLocaleChange={(locale) => {
                store.dispatch(setLocale(locale));
              }}
              fetchTranslations={fetchTranslationsFn}
              updateURL={(newLocale) => {
                const updatedURL = updateUrlParameter(T3_URL_PARAMS.locale, newLocale);
                appHistory.replace(updatedURL);
              }}
              useDefaultOptions={useDefaultTranslationOptions}
            >
              <DashboardReportBlockConfigProvider>
                <ConfirmationDialogContextProvider>
                  <DashboardGlobalFiltersConfigProvider>{children}</DashboardGlobalFiltersConfigProvider>
                </ConfirmationDialogContextProvider>
              </DashboardReportBlockConfigProvider>
            </TranslationsProvider>
          </Provider>
        </SkeletonTheme>
      </SWRConfig>
    </FlagsmithProvider>
  );
};

const App = () => {
  const { isLoading: translationsLoading } = useLocalizationContext();
  if (translationsLoading) return <LoaderBar />;

  return (
    <Router history={appHistory}>
      <ErrorBoundary>
        <ChatwootWidget forcedLocale='en' />
        <NotificationV2 />
        <RouterWarningProvider>
          <Routes />
        </RouterWarningProvider>
      </ErrorBoundary>
    </Router>
  );
};
